'use client';
import { Button, Modal, Text } from '@magicschool/spellbook';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TTSProvider, useTTS } from '@/contexts/TTSProvider';
import { ReadAloud } from '@/contexts/TTSProvider/components/ReadAloud';
import { storage } from '@/features/storage';
import { useStore } from '@/features/store';

export const CodeOfConductModalClient = () => {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const { pageTitleRef, currentRoom, student } = useStore(
    ({ StudentStoreData: s }) => ({
      pageTitleRef: s.pageTitleRef,
      currentRoom: s.currentRoom,
      student: s.student,
    }),
    [],
  );

  const roomId = currentRoom?.id;
  const studentId = student?.id;

  // Wait for store to hydrate before checking/showing the modal
  const isHydrated = Boolean(roomId && studentId);

  useEffect(() => {
    if (!isHydrated || accepted) return;
    const alreadyAccepted = Boolean(storage.getItem(`code-of-conduct-accepted-${roomId}-${studentId}`));
    if (alreadyAccepted) {
      setAccepted(true);
    } else {
      setOpen(true);
    }
  }, [isHydrated, roomId, studentId, accepted]);

  // Persist acceptance for the current IDs — re-runs if IDs change after a store recreation
  useEffect(() => {
    if (!isHydrated || !accepted) return;
    storage.setItem(`code-of-conduct-accepted-${roomId}-${studentId}`, 'true');
  }, [isHydrated, accepted, roomId, studentId]);

  const onAccept = () => {
    setAccepted(true);
    setOpen(false);
  };

  const handleCloseAutoFocus = () => {
    if (pageTitleRef?.current) {
      pageTitleRef.current.focus();
    }
  };

  return (
    <TTSProvider endpoint="/api/speech/tts" defaultOpts={{ rate: 1, lang: 'en-US', voice: 'nova' }}>
      <CodeOfConductModalInner open={open} onAccept={onAccept} onCloseAutoFocus={handleCloseAutoFocus} />
    </TTSProvider>
  );
};

const CodeOfConductModalInner = ({
  open,
  onAccept,
  onCloseAutoFocus,
}: {
  open: boolean;
  onAccept: () => void;
  onCloseAutoFocus: () => void;
}) => {
  const tts = useTTS();
  const intl = useIntl();

  const conductText = useMemo(() => {
    const title = intl.formatMessage({ id: 'conduct.title' });
    const terms = Array.from({ length: 6 }, (_, i) => intl.formatMessage({ id: `conduct.term${i + 1}` }));
    return `${title}\n\n${terms.join('\n\n')}`;
  }, [intl]);

  const handleAccept = () => {
    tts.stop();
    onAccept();
  };

  return (
    <Modal
      size="lg"
      open={open}
      disableCloseIcon
      preventOutsideClick
      onCloseAutoFocus={onCloseAutoFocus}
      data-testid="modal-code-of-conduct"
    >
      <Modal.Header>
        <FormattedMessage id="conduct.title" />
      </Modal.Header>
      <Modal.Content className="flex flex-col gap-4">
        <Text variant="body-2">
          <FormattedMessage id="conduct.term1" values={{ b: (text) => <b key="term1">{text}</b> }} />
        </Text>
        <Text variant="body-2">
          <FormattedMessage id="conduct.term2" values={{ b: (text) => <b key="term2">{text}</b> }} />
        </Text>
        <Text variant="body-2">
          <FormattedMessage id="conduct.term3" values={{ b: (text) => <b key="term3">{text}</b> }} />
        </Text>
        <Text variant="body-2">
          <FormattedMessage id="conduct.term4" values={{ b: (text) => <b key="term4">{text}</b> }} />
        </Text>
        <Text variant="body-2">
          <FormattedMessage id="conduct.term5" values={{ b: (text) => <b key="term5">{text}</b> }} />
        </Text>
        <Text variant="body-2">
          <FormattedMessage id="conduct.term6" values={{ b: (text) => <b key="term6">{text}</b> }} />
        </Text>
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={handleAccept} data-testid="confirm-acknowledgement">
          <FormattedMessage id="conduct.acknowledge" />
        </Button>
        <ReadAloud text={conductText} toolId="code-of-conduct" triggerVariant="outlined" triggerSize="md" />
      </Modal.Footer>
    </Modal>
  );
};
